import axios from 'axios';
import { formatDateTime } from '../utils/wrapDate';
import { sharedRequest } from '../utils/sharedRequest';

const BASE_URL = `${process.env.REACT_APP_API_URL || '/api'}/vehicle-transfers`;

export type TransferStatus = 'PENDING_APPROVAL' | 'APPROVED' | 'COMPLETED' | 'REJECTED' | 'CANCELLED';
export type ApprovalKind = 'SENDING' | 'RECEIVING' | 'FLEET';
export type Decision = 'PENDING' | 'APPROVED' | 'REJECTED';
export type BillingBasis = 'HOURLY' | 'MONTHLY' | 'OTHER';
export type PhotoSide = 'FRONT' | 'BACK' | 'LEFT' | 'RIGHT';
export type TransferListView = 'awaiting-me' | 'open' | 'completed' | 'all';

export const PHOTO_SIDES: PhotoSide[] = ['FRONT', 'BACK', 'LEFT', 'RIGHT'];

export const APPROVAL_KIND_LABEL: Record<ApprovalKind, string> = {
  SENDING: 'Sending Ops Manager',
  RECEIVING: 'Receiving Ops Manager',
  FLEET: 'Fleet',
};

export const TRANSFER_STATUS_LABEL: Record<TransferStatus, string> = {
  PENDING_APPROVAL: 'Awaiting approval',
  APPROVED: 'Approved, awaiting Finance',
  COMPLETED: 'Complete',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};

export interface Person {
  id: number;
  name: string;
}

export interface AreaRef {
  id: number;
  name: string;
  code: string | null;
  label: string;
}

export interface UnitSnapshot {
  unitNumber: string;
  description: string | null;
  make: string | null;
  model: string | null;
  year: number | null;
  vin: string | null;
  plate: string | null;
}

export interface TransferApproval {
  id: number;
  kind: ApprovalKind;
  approver: Person;
  decision: Decision;
  autoApproved: boolean;
  decidedAt: string | null;
  comment: string | null;
}

export interface TransferFile {
  id: number;
  name: string;
  mimeType: string | null;
  sizeBytes: number | null;
  photoSide: PhotoSide | null;
  /** V3-057: Pictures and Documents show apart. */
  kind?: 'picture' | 'document';
  isRecord: boolean;
}

export interface Transfer {
  id: number;
  status: TransferStatus;
  vehicleId: number;
  unit: UnitSnapshot;
  from: AreaRef;
  to: AreaRef;
  toForemanArea: { id: number; name: string } | null;
  toYard: { id: number; name: string; code: string | null } | null;
  effectiveDate: string;
  permanent: boolean;
  estimatedReturnDate: string | null;
  meterOut: number | null;
  meterUnit: 'KM' | 'HR';
  blueBookRate: number | null;
  /** The unit's rate when it was requested (Blue Book rate on older transfers). */
  rate: number | null;
  billingBasis: BillingBasis | null;
  billingOther: string | null;
  conditionNotes: string | null;
  accessories: string | null;
  otherItemsSent: string | null;
  unitFilesNotes: string | null;
  comments: string | null;
  requestedBy: Person;
  requestedAt: string;
  completedBy: Person | null;
  completedAt: string | null;
  sageReference: string | null;
  rejectionReason: string | null;
  cancelledAt: string | null;
  approvals: TransferApproval[];
  approvedCount: number;
  waitingOn: { kind: ApprovalKind; approver: Person }[];
  files: TransferFile[];
  pdfAttachmentId: number | null;
  returnOfTransferId: number | null;
  hasReturn: boolean;
  overdueToReturn: boolean;
  can: {
    decide: ApprovalKind | null;
    cancel: boolean;
    complete: boolean;
    reassign: boolean;
    startReturn: boolean;
    retryPdf: boolean;
  };
  needsMyAction: boolean;
}

export interface TransferEvent {
  id: number;
  eventType: string;
  payload: Record<string, unknown> | null;
  actor: string | null;
  occurredAt: string;
}

export interface TransferDetail extends Transfer {
  canViewUnit: boolean;
  events: TransferEvent[];
}

export interface DestinationArea {
  id: number;
  name: string;
  code: string | null;
  foremanAreas: { id: number; name: string; yards: { id: number; name: string; code: string | null }[] }[];
}

export interface FormDefaults {
  unit: UnitSnapshot & { id: number };
  fromAreas: { id: number; name: string; code: string | null }[];
  defaultFromServiceAreaId: number | null;
  meterOut: number | null;
  meterUnit: 'KM' | 'HR';
  rate: number | null;
  openTransferId: number | null;
  today: string;
  requesterId: number;
}

export interface ApproverPreview {
  approvers: {
    kind: ApprovalKind;
    user: { id: number; name: string; lastName: string } | null;
    areaLabel?: string;
    autoApproved: boolean;
  }[];
  problem: string | null;
}

export interface SettingsPerson {
  id: number;
  name: string;
  lastName: string;
  active: boolean;
}

/** V3-059: counts and up to five transfers for the Pending Transfers Home widget. */
export interface PendingTransfersSummary {
  pendingCount: number;
  needsMyActionCount: number;
  rows: Array<{
    id: number;
    vehicleId: number;
    unitNumber: string;
    from: string;
    to: string;
    effectiveDate: string;
    waitingOn: string[];
    status: string;
    needsMyAction: boolean;
  }>;
}

export interface TransferSettings {
  /** The fleet approver when a service area has none of its own. */
  fleetApprover: SettingsPerson | null;
  /** V3-058: approves in an area's place when it has no Ops Manager (Gord Miller). */
  noOpsManagerApprover: SettingsPerson | null;
  financeEmails: string[];
  /** Copied on approval requests and approved records, without approving. */
  ccEmails: string[];
  areas: Array<{
    serviceAreaId: number;
    name: string;
    code: string | null;
    opsManager: SettingsPerson | null;
    fleetApprover: SettingsPerson | null;
    /** Who the area's Ops Manager slot resolves to today, and from where (V3-058). */
    resolvedOpsManager?: (SettingsPerson & { source: 'setting' | 'role' | 'fallback' }) | null;
  }>;
}

/** One pinned row on the Equipment list. `unit` is the list row when the viewer can open the unit. */
export interface PendingTransferRow<UnitRow = unknown> {
  transfer: Pick<
    Transfer,
    'id' | 'status' | 'from' | 'to' | 'effectiveDate' | 'approvedCount' | 'waitingOn' | 'needsMyAction' | 'unit' | 'vehicleId'
  > & { approvalsTotal: number };
  unit: UnitRow | null;
}

export interface TransferRequestInput {
  vehicleId: number;
  fromServiceAreaId: number | null;
  toServiceAreaId: number | null;
  toForemanAreaId: number | null;
  toYardId: number | null;
  effectiveDate: string;
  /** V3-057: what goes with the unit (buckets, plows). */
  accessories: string;
  comments: string;
  returnOfTransferId: number | null;
}

export class TransferRequestError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

function fail(error: unknown): never {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status ?? 0;
    const message =
      (error.response?.data as { error?: string } | undefined)?.error ||
      (status ? `Request failed (${status})` : 'No response from the server. Check your connection.');
    throw new TransferRequestError(message, status);
  }
  throw error instanceof Error ? error : new Error('Something went wrong');
}

async function call<T>(promise: Promise<{ data: T }>): Promise<T> {
  try {
    return (await promise).data;
  } catch (error) {
    return fail(error);
  }
}

export const vehicleTransferService = {
  list: (view: TransferListView, vehicleId?: number) =>
    // V3-036: one request per view and unit, even when asked twice at once.
    sharedRequest(`vehicle-transfers?view=${view}&vehicleId=${vehicleId ?? ''}`, () =>
      call<{ transfers: Transfer[] }>(axios.get(BASE_URL, { params: { view, vehicleId } }))
    ).then((r) => r.transfers),

  awaitingCount: () =>
    sharedRequest('vehicle-transfers/awaiting-me/count', () => call<{ count: number }>(axios.get(`${BASE_URL}/awaiting-me/count`))).then(
      (r) => r.count
    ),

  /** V3-059: the Pending Transfers Home widget. */
  homeSummary: () => call<PendingTransfersSummary>(axios.get(`${BASE_URL}/home-summary`)),

  get: (id: number) => call<TransferDetail>(axios.get(`${BASE_URL}/${id}`)),

  destinations: () =>
    call<{ serviceAreas: DestinationArea[] }>(axios.get(`${BASE_URL}/destinations`)).then((r) => r.serviceAreas),

  formDefaults: (vehicleId: number) => call<FormDefaults>(axios.get(`${BASE_URL}/form-defaults`, { params: { vehicleId } })),

  approvers: (fromServiceAreaId: number, toServiceAreaId: number) =>
    call<ApproverPreview>(axios.get(`${BASE_URL}/approvers`, { params: { fromServiceAreaId, toServiceAreaId } })),

  /** Pictures (images) and Documents are optional and separate (V3-057). */
  request: (input: TransferRequestInput, files: { pictures: File[]; documents: File[] }) => {
    const form = new FormData();
    form.append('payload', JSON.stringify(input));
    for (const file of files.pictures) form.append('pictures', file);
    for (const file of files.documents) form.append('documents', file);
    return call<TransferDetail>(axios.post(BASE_URL, form));
  },

  decide: (id: number, decision: 'APPROVED' | 'REJECTED', comment: string) =>
    call<TransferDetail>(axios.post(`${BASE_URL}/${id}/decision`, { decision, comment })),

  reassign: (id: number, kind: ApprovalKind, userId: number) =>
    call<TransferDetail>(axios.post(`${BASE_URL}/${id}/reassign`, { kind, userId })),

  cancel: (id: number, reason: string) => call<TransferDetail>(axios.post(`${BASE_URL}/${id}/cancel`, { reason })),

  complete: (id: number, sageReference: string, completedOn: string) =>
    call<TransferDetail>(axios.post(`${BASE_URL}/${id}/complete`, { sageReference, completedOn })),

  retryPdf: (id: number) => call<TransferDetail>(axios.post(`${BASE_URL}/${id}/retry-pdf`)),

  fileUrl: (transferId: number, fileId: number, download = false) =>
    `${BASE_URL}/${transferId}/files/${fileId}${download ? '?download=1' : ''}`,

  settings: () => call<TransferSettings>(axios.get(`${BASE_URL}/settings`)),

  people: (search: string) =>
    call<{ people: { id: number; name: string; role: string | null }[] }>(axios.get(`${BASE_URL}/people`, { params: { search } })).then(
      (r) => r.people
    ),

  updateSettings: (body: {
    fleetApproverUserId?: number;
    noOpsManagerApproverUserId?: number;
    financeEmails?: string;
    ccEmails?: string;
    areas?: Array<{ serviceAreaId: number; opsManagerUserId: number | null; fleetApproverUserId: number | null }>;
  }) =>
    call<TransferSettings>(axios.put(`${BASE_URL}/settings`, body)),
};

/** 'YYYY-MM-DD' calendar date as shown everywhere (FMT-01), without a timezone shift. */
export function formatDay(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : '';
}

/** "2026-06-11 1:16 PM", Pacific (FMT-02, FMT-24). */
export const formatWhen = (iso: string | null | undefined) => formatDateTime(iso ?? null) ?? '';
